import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { User, Lock, Save } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user: me, setUser } = useAuth();

  const [profile, setProfile] = useState({ name: "", email: "", mcube_agent_number: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    setProfile({
      name: me?.name || "",
      email: me?.email || "",
      mcube_agent_number: me?.mcube_agent_number || "",
    });
  }, [me]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim() || !profile.email.trim()) return toast.error("Name and email are required");
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: profile.name.trim(),
        email: profile.email.trim(),
        mcube_agent_number: profile.mcube_agent_number.trim(),
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pw.current_password || !pw.new_password) return toast.error("Fill all password fields");
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change failed");
    } finally {
      setSavingPw(false);
    }
  };

  const G = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };
  const initial = (me?.name || me?.email || "?")[0].toUpperCase();

  return (
    <div className="p-6 max-w-xl mx-auto space-y-6">
      {/* Avatar header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0" style={G}>
          {initial}
        </div>
        <div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">{me?.name || me?.email}</div>
          <div className="text-sm text-gray-500 capitalize">{me?.role}</div>
        </div>
      </div>

      {/* Profile */}
      <div className="bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <User className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-white/70">Profile</span>
        </div>
        <form onSubmit={saveProfile} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">Name</label>
            <input
              className="w-full border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">Email</label>
            <input
              type="email"
              className="w-full border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">MCube Agent Number</label>
            <input
              className="w-full border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={profile.mcube_agent_number}
              onChange={(e) => setProfile((p) => ({ ...p, mcube_agent_number: e.target.value }))}
              placeholder="Optional"
            />
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={G}
          >
            <Save className="h-4 w-4" />
            {savingProfile ? "Saving…" : "Save Profile"}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-white/70">Change Password</span>
        </div>
        <form onSubmit={savePassword} className="space-y-4">
          {[
            { key: "current_password", label: "Current Password" },
            { key: "new_password", label: "New Password" },
            { key: "confirm", label: "Confirm New Password" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">{label}</label>
              <input
                type="password"
                className="w-full border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={pw[key]}
                onChange={(e) => setPw((p) => ({ ...p, [key]: e.target.value }))}
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={savingPw}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={G}
          >
            <Lock className="h-4 w-4" />
            {savingPw ? "Saving…" : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
